import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import './MaturedDealSlipModal.css';

const OUR_COMPANY = 'Sherwood Capital';

const toNum = (v) => {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) =>
  toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

/** Prefer adjusted face when present on a buyback settling leg. */
function resolveLegFace(t, leg) {
  const prefix = leg === 1 ? 'leg1' : 'leg2';
  const adjusted = t?.[`${prefix}_adjusted_face_value`];
  const face = t?.[`${prefix}_face_value`];
  if (adjusted !== null && adjusted !== undefined && adjusted !== '') return adjusted;
  return face;
}

function resolveCounterparty(t) {
  return (
    t.leg2_counterparty_name ||
    t.leg1_counterparty_name ||
    t.counterparty_name ||
    t.counterparty ||
    t.leg2_counterparty ||
    t.leg1_counterparty ||
    ''
  );
}

/**
 * Matured buyback slips settle on leg 2:
 *   Sell/Buy → leg2 Buy details
 *   Buy/Sell → leg2 Sell details
 */
function resolveSlipType(dealType, transaction) {
  const leg1 = String(transaction?.leg1_transaction_type || '').toLowerCase();
  const leg2 = String(transaction?.leg2_transaction_type || '').toLowerCase();
  if (leg1 === 'sell' && leg2 === 'buy') return 'sell_buy';
  if (leg1 === 'buy' && leg2 === 'sell') return 'buy_sell';

  const raw = String(
    transaction?.instrument || transaction?.deal_type || dealType || ''
  ).toLowerCase();

  if (raw.includes('reverse') || raw.includes('repo')) return 'reverse_repo';
  if (raw.includes('buy-sell') || raw.includes('buy/sell')) return 'buy_sell';
  if (raw.includes('sell-buy') || raw.includes('sell/buy')) return 'sell_buy';

  const buyIdx = raw.indexOf('buy');
  const sellIdx = raw.indexOf('sell');
  if (buyIdx >= 0 && sellIdx >= 0) {
    return buyIdx < sellIdx ? 'buy_sell' : 'sell_buy';
  }

  if (raw.includes('gsec') || raw.includes('bond')) return 'gsec';
  return 'gsec';
}

/** Editable Yes/No text row, pre-filled from the deal's fund_movement flag. */
function fundMovementRow(t) {
  return {
    key: 'fund_movement',
    label: 'Fund Movement',
    value: String(t.fund_movement || '').toLowerCase() === 'yes' ? 'Yes' : 'No'
  };
}

/** Swap Seller ↔ Buyer for the roll-over slip; other fields stay identical. */
function swapSellerBuyer(rows) {
  const seller = rows.find((r) => r.key === 'seller');
  const buyer = rows.find((r) => r.key === 'buyer');
  if (!seller || !buyer) return rows;
  return rows.map((r) => {
    if (r.key === 'seller') return { ...r, value: buyer.value };
    if (r.key === 'buyer') return { ...r, value: seller.value };
    return r;
  });
}

function buildFields(slipType, t) {
  const counterparty = resolveCounterparty(t);

  if (slipType === 'reverse_repo') {
    const rawType = String(t.deal_type || t.instrument || '').toLowerCase();
    return {
      // Show the actual deal type: plain "Repo" unless it is a Reverse Repo.
      dealTypeLabel: rawType.includes('reverse') || !rawType.includes('repo') ? 'Reverse Repo' : 'Repo',
      rows: [
        { key: 'value_date', label: 'Value Date', value: fmtDate(t.value_date) },
        { key: 'counterparty', label: 'Counterparty', value: counterparty },
        { key: 'maturity_date', label: 'Maturity Date', value: fmtDate(t.maturity_date) },
        { key: 'principal_amount', label: 'Principal Amount', value: fmtMoney(t.principal_amount) },
        { key: 'rate', label: 'Rate %', value: String(t.rate ?? t.interest_rate ?? '') },
        fundMovementRow(t)
      ],
      hasCollateral: true
    };
  }

  // Sell/Buy matured slip = settling Buy leg (leg 2).
  if (slipType === 'sell_buy') {
    return {
      dealTypeLabel: 'Sell/Buy',
      rows: [
        { key: 'seller', label: 'Seller', value: counterparty || '' },
        { key: 'buyer', label: 'Buyer', value: OUR_COMPANY },
        {
          key: 'face_value',
          label: 'Face Value',
          value: fmtMoney(resolveLegFace(t, 2) ?? t.face_value ?? t.leg1_face_value)
        },
        {
          key: 'isin',
          label: 'Maturity',
          value: t.leg2_isin || t.leg1_isin || t.isin_number || t.isin || ''
        },
        {
          key: 'ytm',
          label: 'Yield to Maturity %',
          value: String(t.leg2_yield_rate ?? t.yield ?? t.yield_rate ?? t.leg1_yield_rate ?? '')
        },
        {
          key: 'rate',
          label: 'Rate',
          value: String(t.leg1_interest_rate ?? t.rate ?? 0)
        },
        {
          key: 'settlement',
          label: 'Settlement Value',
          value: fmtMoney(
            t.leg2_settlement_amount ??
              t.settlement_value ??
              t.settlement_amount ??
              t.leg1_settlement_amount ??
              t.maturity_value
          )
        },
        {
          key: 'maturity_date',
          label: 'Maturity Date',
          value: fmtDate(t.leg2_value_date || t.maturity_date)
        },
        fundMovementRow(t)
      ],
      hasCollateral: false
    };
  }

  // Buy/Sell matured slip = settling Sell leg (leg 2).
  if (slipType === 'buy_sell') {
    return {
      dealTypeLabel: 'Buy/Sell',
      rows: [
        { key: 'seller', label: 'Seller', value: OUR_COMPANY },
        { key: 'buyer', label: 'Buyer', value: counterparty || '' },
        {
          key: 'face_value',
          label: 'Face Value',
          value: fmtMoney(resolveLegFace(t, 2) ?? t.face_value ?? t.leg1_face_value)
        },
        {
          key: 'isin',
          label: 'Maturity',
          value: t.leg2_isin || t.leg1_isin || t.isin_number || t.isin || ''
        },
        {
          key: 'ytm',
          label: 'Yield to Maturity %',
          value: String(t.leg2_yield_rate ?? t.yield ?? t.yield_rate ?? t.leg1_yield_rate ?? '')
        },
        {
          key: 'rate',
          label: 'Rate',
          value: String(t.leg1_interest_rate ?? t.rate ?? 0)
        },
        {
          key: 'settlement',
          label: 'Settlement Value',
          value: fmtMoney(
            t.leg2_settlement_amount ??
              t.settlement_value ??
              t.settlement_amount ??
              t.leg1_settlement_amount ??
              t.maturity_value
          )
        },
        {
          key: 'maturity_date',
          label: 'Maturity Date',
          value: fmtDate(t.leg2_value_date || t.maturity_date)
        },
        fundMovementRow(t)
      ],
      hasCollateral: false
    };
  }

  // gsec
  return {
    dealTypeLabel: 'GSEC',
    rows: [
      { key: 'seller', label: 'Seller', value: OUR_COMPANY },
      { key: 'buyer', label: 'Buyer', value: counterparty },
      { key: 'face_value', label: 'Face Value', value: fmtMoney(t.face_value) },
      { key: 'isin', label: 'Maturity', value: t.isin_number || t.isin || '' },
      { key: 'ytm', label: 'Yield to Maturity %', value: String(t.yield ?? t.yield_rate ?? '') },
      {
        key: 'settlement',
        label: 'Settlement Value',
        value: fmtMoney(t.settlement_value ?? t.settlement_amount ?? t.maturity_value)
      },
      fundMovementRow(t)
    ],
    hasCollateral: false
  };
}

const MaturedDealSlipModal = ({ transaction, dealType, onClose, variant = 'matured' }) => {
  const slipType = useMemo(() => resolveSlipType(dealType, transaction), [dealType, transaction]);
  const initial = useMemo(() => {
    const fields = buildFields(slipType, transaction || {});
    if (variant === 'rollover') {
      return { ...fields, rows: swapSellerBuyer(fields.rows) };
    }
    return fields;
  }, [slipType, transaction, variant]);

  const [dealTypeLabel, setDealTypeLabel] = useState(initial.dealTypeLabel);
  const [rows, setRows] = useState(initial.rows);
  const [slipNumber, setSlipNumber] = useState('');
  const [authorizedBy, setAuthorizedBy] = useState('');
  const printRef = useRef(null);

  const [collateral, setCollateral] = useState(() =>
    initial.hasCollateral
      ? (transaction?.isins || []).map((r) => ({
          isin: r.isin || r.isin_number || '',
          face: fmtMoney(r.faceValue ?? r.face_value)
        }))
      : []
  );

  // Keep fields in sync if a different deal is opened without remounting.
  useEffect(() => {
    setDealTypeLabel(initial.dealTypeLabel);
    setRows(initial.rows);
    setCollateral(
      initial.hasCollateral
        ? (transaction?.isins || []).map((r) => ({
            isin: r.isin || r.isin_number || '',
            face: fmtMoney(r.faceValue ?? r.face_value)
          }))
        : []
    );
  }, [initial, transaction]);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_BASE_URL}/maturity/matured-slip-number`)
      .then((res) => {
        if (active && res.data?.success) setSlipNumber(res.data.slipNumber);
      })
      .catch(() => {
        // Non-fatal: leave the number editable/blank if the sequence call fails.
      });
    return () => {
      active = false;
    };
  }, []);

  const updateRow = (idx, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, value } : r)));
  };

  const updateCollateral = (idx, field, value) => {
    setCollateral((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addCollateralRow = () => setCollateral((prev) => [...prev, { isin: '', face: '' }]);
  const removeCollateralRow = (idx) => setCollateral((prev) => prev.filter((_, i) => i !== idx));

  const collateralTotal = collateral.reduce((s, r) => s + toNum(r.face), 0);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="matured-slip-overlay" role="dialog" aria-modal="true">
      <div className="matured-slip-modal">
        <div className="matured-slip-actions">
          <button className="msl-btn primary" onClick={handlePrint}>Print</button>
          <button className="msl-btn" onClick={onClose}>Close</button>
        </div>

        <div className="matured-slip-printable" ref={printRef}>
          <div className="msl-header">
            <div className="msl-deal-type">
              <span className="msl-dt-label">Deal Type</span>
              <input
                className="msl-input msl-dt-input"
                value={dealTypeLabel}
                onChange={(e) => setDealTypeLabel(e.target.value)}
              />
            </div>
            <div className="msl-slip-no">
              <input
                className="msl-input msl-slip-no-input"
                value={slipNumber}
                onChange={(e) => setSlipNumber(e.target.value)}
                placeholder="YYYYMM-####"
              />
            </div>
          </div>

          <table className="msl-fields">
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.key}>
                  <td className="msl-field-label">{row.label}</td>
                  <td className="msl-field-value">
                    <input
                      className="msl-input"
                      value={row.value}
                      onChange={(e) => updateRow(idx, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {initial.hasCollateral && (
            <div className="msl-collateral">
              <div className="msl-collateral-title">Collateral Allocation (ISIN-wise Face Value)</div>
              <table className="msl-collateral-table">
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>#</th>
                    <th>ISIN</th>
                    <th style={{ width: '35%' }}>Face Value Allocated</th>
                    <th className="msl-no-print" style={{ width: '8%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {collateral.map((r, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <input
                          className="msl-input"
                          value={r.isin}
                          onChange={(e) => updateCollateral(idx, 'isin', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="msl-input msl-num"
                          value={r.face}
                          onChange={(e) => updateCollateral(idx, 'face', e.target.value)}
                        />
                      </td>
                      <td className="msl-no-print">
                        <button className="msl-row-remove" onClick={() => removeCollateralRow(idx)}>×</button>
                      </td>
                    </tr>
                  ))}
                  <tr className="msl-collateral-total">
                    <td colSpan={2}>Total</td>
                    <td className="msl-num">{fmtMoney(collateralTotal)}</td>
                    <td className="msl-no-print"></td>
                  </tr>
                </tbody>
              </table>
              <button className="msl-btn msl-no-print" onClick={addCollateralRow}>+ Add ISIN row</button>
            </div>
          )}

          <div className="msl-signature">
            <div className="msl-sign-line">…….........................................................</div>
            <div className="msl-sign-row">
              <span className="msl-sign-label">Authorized By</span>
              <input
                className="msl-input msl-sign-input"
                value={authorizedBy}
                onChange={(e) => setAuthorizedBy(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaturedDealSlipModal;
